// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import { CheckCircle2 } from "lucide-react"
import type { DayLedgerResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { Button } from "@/components/ui/button"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import { useI18n } from "@/i18n"

/**
 * Morning backfill card (owner-approved 2026-08-30, mirror of the RN
 * BackfillCard): if yesterday still has unlogged meals, ask "How did
 * yesterday actually go?" with one-tap answers. TRUTH-COLLECTION ONLY —
 * "I had it all" records outcomes because the USER tapped it; "Leave it"
 * keeps the day honestly unlogged (dismissed once per day, device-local).
 */

function yesterdayIso(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

export function BackfillCard({ onLogged }: { onLogged?: () => void }) {
  const api = useApiClient()
  const { t } = useI18n()
  const [ledger, setLedger] = useState<DayLedgerResponse | null>(null)
  const [phase, setPhase] = useState<"idle" | "saving" | "done" | "hidden">("idle")
  const date = yesterdayIso()

  useEffect(() => {
    let cancelled = false
    try {
      if (window.localStorage.getItem(`bb_backfill_dismissed_${date}`) === "1") {
        setPhase("hidden")
        return
      }
    } catch {
      /* storage miss just re-shows the card */
    }
    api
      .getDayLedger(date)
      .then(l => {
        if (!cancelled) setLedger(l)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [api, date])

  const unlogged = (ledger?.plannedMeals ?? []).filter(m => m.outcome === null)

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(`bb_backfill_dismissed_${date}`, "1")
    } catch {
      /* card simply reappears next load */
    }
    setPhase("hidden")
  }, [date])

  const markAllPlanned = useCallback(async () => {
    setPhase("saving")
    try {
      for (const meal of unlogged) {
        await api.recordMealOutcome(meal.mealId, { outcome: "had" })
      }
      setPhase("done")
      onLogged?.()
      window.setTimeout(dismiss, 1800)
    } catch {
      setPhase("idle")
    }
  }, [api, unlogged, dismiss, onLogged])

  if (phase === "hidden" || !ledger || unlogged.length === 0) return null

  return (
    <div className="anim-rise flex flex-col gap-3 rounded-bb-xl bg-green p-5 text-paper shadow-lg">
      {phase === "done" ? (
        <p className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="size-5 text-lime" aria-hidden="true" />
          {t("views.today.backfillDone")}
        </p>
      ) : (
        <>
          <p className="font-display text-lg font-semibold">{t("views.today.backfillTitle")}</p>
          <p className="text-sm text-paper/80">
            {t("views.today.backfillBody", { count: unlogged.length })}
          </p>
          <Button
            type="button"
            disabled={phase === "saving"}
            onClick={() => void markAllPlanned()}
            className="min-h-11 w-full bg-lime font-bold text-ink hover:bg-lime/90"
          >
            {t("views.today.backfillAllPlanned")}
          </Button>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="min-h-10 flex-1 border-paper/40 bg-transparent text-xs font-semibold text-paper hover:bg-paper/10 hover:text-paper"
              onClick={() => {
                dismiss()
                openAmyWithPrompt(t("views.today.backfillChangedPrompt"))
              }}
            >
              {t("views.today.backfillChanged")}
            </Button>
            <button
              type="button"
              className="min-h-10 px-2 text-xs font-semibold text-paper/60 hover:text-paper"
              onClick={dismiss}
            >
              {t("views.today.backfillDismiss")}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
